import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { SubscriptionPlan, CreateClientForm } from '../../types';
import BkashPaymentModal from '../../components/payment/BkashPaymentModal';

type PaymentMethod = 'bkash' | 'manual';

const ResellerCreateClient: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bkash');
  const [showBkashModal, setShowBkashModal] = useState(false);

  const [form, setForm] = useState<CreateClientForm>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    companyName: '',
    planId: '',
    paymentReference: '',
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoadingPlans(true);
    const response = await api.getPublicPlans();
    if (response.success && response.data) {
      const data = response.data;
      setPlans(data.filter(p => p.isActive));
      if (data.length > 0) {
        setForm(f => ({ ...f, planId: data[0].id }));
      }
    }
    setLoadingPlans(false);
  };

  const validateForm = () => {
    if (!form.email || !form.password || !form.planId) {
      setError('Email, password, and plan are required');
      return false;
    }

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleBkashPayment = () => {
    if (!validateForm()) return;
    setShowBkashModal(true);
  };

  const handlePaymentComplete = (_result: {
    paymentId: string;
    trxId: string;
    subscriptionId: string;
    user?: any;
  }) => {
    setShowBkashModal(false);
    setSuccess(true);
    setTimeout(() => {
      navigate('/reseller/clients');
    }, 2000);
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    const response = await api.createClient(form);

    if (response.success) {
      setSuccess(true);
      setTimeout(() => {
        navigate('/reseller/clients');
      }, 2000);
    } else {
      setError(response.error || 'Failed to create client');
    }

    setLoading(false);
  };

  const selectedPlan = plans.find(p => p.id === form.planId);

  if (success) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="bg-[#121212] border border-emerald-500/30 rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✅</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Client Created!</h2>
          <p className="text-neutral-400 mb-4">
            {paymentMethod === 'bkash'
              ? 'Payment received and subscription activated successfully.'
              : 'The client account has been created and is pending admin approval.'}
          </p>
          <p className="text-neutral-500 text-sm">Redirecting to clients list...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Create New Client</h1>
        <p className="text-neutral-500">Register a new client with a subscription plan</p>
      </div>

      <form onSubmit={handleManualSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6 space-y-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Client Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    required
                    className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
                    placeholder="client@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Password *</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                    required
                    minLength={6}
                    className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">First Name</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))}
                    className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))}
                    className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Company Name</label>
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={(e) => setForm(f => ({ ...f, companyName: e.target.value }))}
                    className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
                    placeholder="ABC Company Ltd"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
                    placeholder="+880 1712-345678"
                  />
                </div>
              </div>
            </div>

            {/* Select Plan */}
            <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Select Plan *</h3>

              {loadingPlans ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {plans.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, planId: plan.id }))}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        form.planId === plan.id
                          ? 'bg-emerald-500/10 border-emerald-500/50'
                          : 'bg-[#1a1a1a] border-[#262626] hover:border-[#363636]'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-bold text-white">{plan.name}</p>
                        {plan.isFeatured && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] font-bold rounded">
                            POPULAR
                          </span>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-white">
                        ৳{plan.priceBdt.toLocaleString()}
                        <span className="text-neutral-500 text-sm font-normal">/mo</span>
                      </p>
                      <p className="text-neutral-500 text-xs mt-2">{plan.features.length} features</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Payment Method</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('bkash')}
                  className={`p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${
                    paymentMethod === 'bkash'
                      ? 'bg-pink-500/10 border-pink-500/50'
                      : 'bg-[#1a1a1a] border-[#262626] hover:border-[#363636]'
                  }`}
                >
                  <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">💳</span>
                  </div>
                  <div>
                    <p className="font-bold text-white">Pay with bKash</p>
                    <p className="text-neutral-500 text-xs">Instant payment & activation</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('manual')}
                  className={`p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${
                    paymentMethod === 'manual'
                      ? 'bg-blue-500/10 border-blue-500/50'
                      : 'bg-[#1a1a1a] border-[#262626] hover:border-[#363636]'
                  }`}
                >
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">📝</span>
                  </div>
                  <div>
                    <p className="font-bold text-white">Manual Payment</p>
                    <p className="text-neutral-500 text-xs">Enter reference (Admin approval needed)</p>
                  </div>
                </button>
              </div>

              {paymentMethod === 'manual' && (
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Payment Reference</label>
                  <input
                    type="text"
                    value={form.paymentReference}
                    onChange={(e) => setForm(f => ({ ...f, paymentReference: e.target.value }))}
                    className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50"
                    placeholder="bKash TXN ID, bank reference, etc."
                  />
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Submit */}
            {paymentMethod === 'bkash' ? (
              <button
                type="button"
                onClick={handleBkashPayment}
                disabled={loading || !form.planId}
                className={`w-full py-4 rounded-xl font-medium transition-all flex items-center justify-center gap-3 ${
                  loading || !form.planId
                    ? 'bg-pink-600/50 text-white/50 cursor-not-allowed'
                    : 'bg-pink-500 hover:bg-pink-600 text-white'
                }`}
              >
                <span className="text-xl">💳</span>
                <span>Pay ৳{selectedPlan?.priceBdt.toLocaleString() || 0} with bKash</span>
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 rounded-xl font-medium transition-all ${
                  loading
                    ? 'bg-emerald-600/50 text-white/50 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                }`}
              >
                {loading ? 'Creating Client...' : 'Create Client'}
              </button>
            )}
          </div>

          {/* Plan Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6 sticky top-6">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Order Summary</h3>

              {selectedPlan ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-neutral-400">Plan</p>
                    <p className="text-white font-medium">{selectedPlan.name}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-neutral-400">Price</p>
                    <p className="text-white font-medium">৳{selectedPlan.priceBdt.toLocaleString()}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-neutral-400">Duration</p>
                    <p className="text-white font-medium">{selectedPlan.durationDays} days</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-neutral-400">Daily Limit</p>
                    <p className="text-white font-medium">{selectedPlan.maxRequestsPerDay} requests</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-neutral-400">Monthly Limit</p>
                    <p className="text-white font-medium">{selectedPlan.maxRequestsPerMonth.toLocaleString()} requests</p>
                  </div>

                  <div className="pt-4 border-t border-[#262626]">
                    <p className="text-neutral-400 text-sm mb-2">Included Features:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedPlan.features.slice(0, 5).map((f, idx) => (
                        <span key={idx} className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded">
                          {f}
                        </span>
                      ))}
                      {selectedPlan.features.length > 5 && (
                        <span className="px-2 py-1 bg-[#1a1a1a] text-neutral-400 text-xs rounded">
                          +{selectedPlan.features.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#262626]">
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-bold text-white">Total</p>
                      <p className="text-lg font-bold text-emerald-400">৳{selectedPlan.priceBdt.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-neutral-500 text-center py-4">Select a plan</p>
              )}

              <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <p className="text-blue-400 text-xs">
                  {paymentMethod === 'bkash' ? (
                    <>
                      <strong>bKash Payment:</strong> Client will be activated immediately after successful payment.
                    </>
                  ) : (
                    <>
                      <strong>Note:</strong> The client account will be created with pending status.
                      Admin approval is required before the client can access the platform.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* bKash Payment Modal */}
      {selectedPlan && (
        <BkashPaymentModal
          isOpen={showBkashModal}
          onClose={() => setShowBkashModal(false)}
          plan={selectedPlan}
          clientData={{
            email: form.email,
            password: form.password,
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone,
            companyName: form.companyName,
          }}
          resellerId={user?.id}
          onPaymentComplete={handlePaymentComplete}
        />
      )}
    </div>
  );
};

export default ResellerCreateClient;
